import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import * as api from '../api/client'
import KPIChart, { KPI_DEFS, teamColor } from './KPIChart'
import TeamsActivityTimeline from './TeamsActivityTimeline'
import type { SessionHistoryResponse } from '../types'

/**
 * Facilitator-only debrief view. Overlays every team's trajectory on each
 * KPI, with a toggle to reveal hidden stocks that were not shown during
 * play (Resistance, Change Capacity, Talent Skills, Governance Maturity,
 * Leadership Capability, AI Capability, Risk Stock).
 *
 * This is the "classic Sterman reveal" moment: after Round 1, the
 * facilitator opens this panel to show teams what was happening behind
 * the dashboard — and why the loop they couldn't see determined their
 * outcome.
 */
export default function DebriefPanel({ sessionId }: { sessionId: string }) {
  const [revealHidden, setRevealHidden] = useState(false)
  const [showTimeline, setShowTimeline] = useState(true)
  const [hiddenTeamIds, setHiddenTeamIds] = useState<Set<string>>(() => new Set())

  const toggleTeam = (tid: string) => {
    setHiddenTeamIds((prev) => {
      const next = new Set(prev)
      if (next.has(tid)) next.delete(tid); else next.add(tid)
      return next
    })
  }
  const showAllTeams = () => setHiddenTeamIds(new Set())

  const historyQ = useQuery({
    queryKey: ['session-history', sessionId],
    queryFn: () => api.getSessionHistory(sessionId),
    refetchInterval: 10_000,
  })
  const catalogQ = useQuery({
    queryKey: ['activities-catalog'],
    queryFn: () => api.getActivitiesCatalog(),
    staleTime: 60_000,
  })

  if (historyQ.isLoading || !historyQ.data) {
    return (
      <div className="card">
        <h2 className="text-lg font-semibold mb-2">Debrief — Trajectory comparison</h2>
        <p className="text-sm text-slate-500 italic">Loading trajectories…</p>
      </div>
    )
  }

  const h = historyQ.data
  const allTeams = h.team_order
    .map(tid => h.teams[tid])
    .filter((t): t is NonNullable<typeof t> => Boolean(t))
  const teams = allTeams.filter(t => !hiddenTeamIds.has(t.team_id))
  const visibleHistory: SessionHistoryResponse = {
    ...h,
    team_order: h.team_order.filter(tid => !hiddenTeamIds.has(tid)),
    teams: Object.fromEntries(
      Object.entries(h.teams).filter(([tid]) => !hiddenTeamIds.has(tid))
    ),
  }

  if (allTeams.length === 0 || allTeams.every(t => t.snapshots.length === 0)) {
    return (
      <div className="card">
        <h2 className="text-lg font-semibold mb-2">Debrief — Trajectory comparison</h2>
        <p className="text-sm text-slate-500 italic">
          Charts will appear after the first quarter is advanced.
        </p>
      </div>
    )
  }

  const visibleKPIs = KPI_DEFS.filter(k => k.visibleToTeam)
  const hiddenKPIs = KPI_DEFS.filter(k => !k.visibleToTeam)
  const kpisToShow = revealHidden ? [...visibleKPIs, ...hiddenKPIs] : visibleKPIs

  return (
    <div className="card">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-3">
        <h2 className="text-lg font-semibold">
          Debrief — Trajectory comparison
          <span className="ml-2 text-sm font-normal text-slate-500">
            · through Q{h.current_quarter} / {h.num_quarters}
          </span>
        </h2>
        <label className="flex items-center gap-2 text-sm cursor-pointer select-none">
          <input
            type="checkbox"
            checked={revealHidden}
            onChange={(e) => setRevealHidden(e.target.checked)}
          />
          <span>Reveal hidden stocks</span>
          <span className="text-xs text-slate-500">(Resistance, Change Capacity, …)</span>
        </label>
      </div>

      <TeamLegend
        history={h}
        hiddenTeamIds={hiddenTeamIds}
        onToggle={toggleTeam}
        onShowAll={showAllTeams}
      />

      {revealHidden && (
        <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-3 py-2 mb-3">
          🔒 Hidden stocks revealed. These were invisible to teams during play.
          This is the "what was really happening" reveal moment of the debrief.
        </p>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
        {kpisToShow.map(kpi => (
          <KPIChart
            key={kpi.key}
            kpi={kpi}
            teams={teams}
            teamOrder={h.team_order}
            currentQuarter={h.current_quarter}
            showEventMarkers={true}
            height={180}
          />
        ))}
      </div>

      <div className="mt-8 pt-6 border-t border-slate-200">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-base font-semibold">Activity timeline</h3>
          <button
            type="button"
            onClick={() => setShowTimeline((v) => !v)}
            className="text-xs text-brand-700 hover:underline"
          >
            {showTimeline ? 'Hide' : 'Show'}
          </button>
        </div>
        {showTimeline && (
          catalogQ.data ? (
            <TeamsActivityTimeline history={visibleHistory} catalog={catalogQ.data} />
          ) : (
            <p className="text-sm text-slate-500 italic">Loading activity catalog…</p>
          )
        )}
      </div>
    </div>
  )
}

function TeamLegend({
  history, hiddenTeamIds, onToggle, onShowAll,
}: {
  history: SessionHistoryResponse
  hiddenTeamIds: Set<string>
  onToggle: (tid: string) => void
  onShowAll: () => void
}) {
  const anyHidden = hiddenTeamIds.size > 0
  return (
    <div className="flex flex-wrap items-center gap-x-2 gap-y-2 mb-4 text-sm">
      {history.team_order.map((tid, i) => {
        const team = history.teams[tid]
        if (!team) return null
        const hidden = hiddenTeamIds.has(tid)
        return (
          <button
            key={tid}
            type="button"
            onClick={() => onToggle(tid)}
            aria-pressed={!hidden}
            title={hidden ? 'Click to show this team' : 'Click to hide this team'}
            className={`inline-flex items-center gap-2 rounded-full border px-2.5 py-1 transition-colors ${
              hidden
                ? 'border-slate-200 bg-slate-50 text-slate-400 hover:bg-slate-100'
                : 'border-slate-300 bg-white text-slate-800 hover:bg-slate-50'
            }`}
          >
            <span
              aria-hidden
              className={`inline-block w-4 h-2 rounded-sm ${hidden ? 'opacity-30' : ''}`}
              style={{ backgroundColor: teamColor(i) }}
            />
            <span className={`font-medium ${hidden ? 'line-through' : ''}`}>
              {team.team_name}
            </span>
            <span className="text-xs font-mono text-slate-500">· {team.snapshots.length}q</span>
          </button>
        )
      })}
      {anyHidden && (
        <button
          type="button"
          onClick={onShowAll}
          className="text-xs text-brand-700 hover:underline ml-1"
        >
          Show all
        </button>
      )}
    </div>
  )
}
